<template>
    <section class="multi-step-filter-popover sm-s">
        <MultiStepFilter :title="title"
            :pubkey="pubkey"
            :items="items"
            :selectedItems="selectedItems"
            :restrictedItems="restrictedItems"
            :max="max"
            :min="min"
            :includeGroups="includeGroups"
            :hideSearch="hideSearch"
            :hideBreadcrumbs="hideBreadcrumbs"
            :showClearSelection="showClearSelection"
            :allowGroupSelection="allowGroupSelection"
            :mode="mode"
            :showBadgeCounts="showBadgeCounts"
            :keepPartialGroups="keepPartialGroups"
            :isolateSelection="isolateSelection"
            :startingItem="startingItem"
            :uiHeight="uiHeight"
            @onCheck="onCheck"
            @onItemSelected="onItemSelected">
            <template #actions>
                <ButtonIcon class="el--transparent el--s-s sm-l-m"
                    icon="cross"
                    @click.prevent.stop="onClose" />
            </template>
        </MultiStepFilter>
    </section>
</template>
<script>
import MultiStepFilter from '../multi-step-filter/MultiStepFilter.vue'
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import GlobalPopoverMixin from '../../globals/GlobalPopoverMixin';

export default {
    name: 'mead-multi-step-filter-popover',
    mixins: [GlobalPopoverMixin],
    components: {ButtonIcon, MultiStepFilter},
    props: {
        /**
         * Title displayed at the top of the popover's filter panel.
         */
        title: {type: String, default: 'mead.options' },
        /**
         * The public key used to publish selection/check events on the global Flash bus so the
         * component that opened this popover (identified by the same key) can receive them.
         */
        pubkey: {type: String, default: '' },
        /**
         * The hierarchical list of selectable items rendered by the inner MultiStepFilter.
         */
        items: {type: Array, default: () => []},
        /**
         * The values currently selected, forwarded to the inner MultiStepFilter to seed its
         * selection state.
         */
        selectedItems: {type: Array, default: () => []},
        /**
         * Items that cannot be selected, forwarded to the inner MultiStepFilter.
         */
        restrictedItems: {type: Array, default: () => []},
        /**
         * Maximum number of items that may be selected; forwarded to the inner MultiStepFilter.
         */
        max: {type: Number, default: 0},
        /**
         * Minimum number of items that must remain selected; forwarded to the inner MultiStepFilter.
         */
        min: {type: Number, default: 0},
        /**
         * Whether group items are included as selectable entries; forwarded to the inner
         * MultiStepFilter.
         */
        includeGroups: {type: Boolean, default: true},
        /**
         * Hide the search input above the list; forwarded to the inner MultiStepFilter.
         */
        hideSearch: {type: Boolean, default: false},
        /**
         * Hide the breadcrumb trail above the list; forwarded to the inner MultiStepFilter.
         * Only use with flat lists, not grouped data: without breadcrumbs there is no way to
         * navigate back out of a group.
         */
        hideBreadcrumbs: {type: Boolean, default: false},
        /**
         * Show a "Clear" action that removes the entire selection; forwarded to the inner
         * MultiStepFilter.
         */
        showClearSelection: {type: Boolean, default: false},
        /**
         * Whether an entire group can be selected as a single value; forwarded to the inner
         * MultiStepFilter.
         */
        allowGroupSelection: {type: Boolean, default: false},
        /**
         * The selection mode passed to the inner MultiStepFilter (e.g. single vs. multi selection
         * vs. no selection), controlling how choices are applied and when the popover auto-closes.
         */
        mode: {type: String, default: () => MeadCommon.MUTLI_STEP_FILTER_MODES.NO_SELECT},
        /**
         * Whether to show badges with selected-descendant counts on group items; forwarded to the
         * inner MultiStepFilter.
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * Whether partially-selected groups should be kept as-is rather than fully expanded;
         * forwarded to the inner MultiStepFilter.
         */
        keepPartialGroups: {type: Boolean, default: false},
        /**
         * Whether selecting a group should only include the group value itself rather than its
         * children's values; forwarded to the inner MultiStepFilter.
         */
        isolateSelection: {type: Boolean, default: false},
        /**
         * The stacking index of this popover, used so any popover it opens can compute the index
         * it should stack above.
         */
        popoverIndex: {type: [Number, String], default: 1},
        /**
         * The item the inner MultiStepFilter should navigate to/preselect on open.
         */
        startingItem: {type: String, default: undefined},
        /**
         * Height of the inner filter's scrollable item list.
         */
        uiHeight: {type: [String, Number], default: '40'},
    },
    methods: {
        onItemSelected: function (value, item) {
            MeadCommon.Flash.publish(`${this.pubkey}-mstp-selected`, {value: value, item: item});
            this.closePopover();
        },
        onCheck: function (metrics) {
            MeadCommon.Flash.publish(`${this.pubkey}-mstp-checked`, metrics);
        },
        onClose: function() {
            this.closePopover();
        }
    },
};
</script>
<style lang="scss" scoped>
.multi-step-filter-popover {
    width: 40rem;
    height: 40rem;
    overflow: auto;
}
</style>
